import React from 'react'
import UploadIcon from 'assets/svgr-icons/Upload.svg'
import MyFilesIcon from 'assets/svgr-icons/My_Files.svg'
import { DotsLoading, Text } from 'common-components'
import DragDropUpload from 'components/DragDropUpload'
import MediaUploadButton from 'components/MediaUploadButton'
import { useDragDropUploadContext } from 'context/DragDropUpload'
import { translate } from 'utils/translations'
import { SMALL_DESKTOP_SIZE_BREAKPOINT } from 'consts'
import { IProps, IUploadAreaExtraAction } from './types'
import { T } from 'consts/translationKeys'
import { useResponsive } from 'hooks/useResponsive'
import * as Styled from './styled'
import { COLORS } from 'consts/colors'

const LoadingComponent = () => (
  <Styled.UploadContent>
    <DotsLoading size="xSmall" dotColor={COLORS.primaryDark} />
  </Styled.UploadContent>
)

const UploadAreaContent: React.FC<IProps> = ({
  mediaLibConfig,
  handleFilesUpload,
  acceptType = 'visualMedia',
  handleMediaLibFiles,
  isLoading,
  extraActions
}) => {
  const { dragDropUploadData } = useDragDropUploadContext()
  const { isDragActive } = dragDropUploadData
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  const onMyFilesClick = (e: React.MouseEvent<HTMLButtonElement>) => {
    e?.preventDefault?.()
    e?.stopPropagation?.()
    window.openMediaLib({
      fileType: acceptType,
      onClose: window.closeMediaLib,
      mediaLibUnmountCb: window.closeMediaLib,
      onChoose: handleMediaLibFiles,
      isVisible: true,
      ...mediaLibConfig
    })
  }

  const onExtraActionClick =
    (action: IUploadAreaExtraAction) =>
    (e: React.MouseEvent<HTMLButtonElement>) => {
      e?.preventDefault?.()
      e?.stopPropagation?.()
      action.onClick()
    }

  const extraActionButtons = extraActions?.map(action => (
    <Styled.Button
      key={action.key}
      icon={action.icon}
      onClick={onExtraActionClick(action)}
      label={action.title}
    />
  ))

  if (isMobile) {
    return (
      <Styled.MobileUploadAreaContainer>
        {isLoading ? (
          <LoadingComponent />
        ) : (
          <>
            <Styled.Button
              icon={<MyFilesIcon />}
              onClick={onMyFilesClick}
              label={translate(T.MY_FILES_LABEL)}
            />
            <MediaUploadButton
              acceptType={acceptType}
              handleFilesUpload={handleFilesUpload}
            >
              <Styled.Button
                icon={<UploadIcon />}
                label={translate(T.UPLOAD_LABEL)}
              />
            </MediaUploadButton>
            {extraActionButtons}
          </>
        )}
      </Styled.MobileUploadAreaContainer>
    )
  }

  return (
    <MediaUploadButton
      acceptType={acceptType}
      handleFilesUpload={handleFilesUpload}
    >
      <Styled.UploadAreaContainer $isDragActive={isDragActive}>
        {isDragActive ? (
          <Styled.DragActiveContent>
            <Text type="h2" color="secondarySemiDark">
              {translate(T.YES_DROP_LABEL)}
            </Text>
          </Styled.DragActiveContent>
        ) : isLoading ? (
          <LoadingComponent />
        ) : (
          <Styled.UploadContent>
            <Text type="h2" color="secondarySemiDark">
              {translate(T.DRAG_DROP_TEXT)}
            </Text>

            <Styled.TextWithLines type="h4" color="secondary">
              {translate(T.OR_LABEL)}
            </Styled.TextWithLines>

            <Styled.ButtonsRow>
              <Styled.Button
                icon={<MyFilesIcon />}
                onClick={onMyFilesClick}
                label={translate(T.MY_FILES_LABEL)}
              />

              <Styled.Button
                icon={<UploadIcon />}
                label={translate(T.UPLOAD_LABEL_SIDEBAR)}
              />

              {extraActionButtons}
            </Styled.ButtonsRow>
          </Styled.UploadContent>
        )}
      </Styled.UploadAreaContainer>
    </MediaUploadButton>
  )
}

export const UploadArea: React.FC<IProps> = ({
  acceptType = 'visualMedia',
  multiple = true,
  style,
  wrapperRef,
  mediaLibConfig,
  handleFilesUpload,
  handleMediaLibFiles,
  isLoading,
  showWarning,
  extraActions
}) => {
  const isMobile = useResponsive(SMALL_DESKTOP_SIZE_BREAKPOINT)

  return (
    <DragDropUpload
      style={style}
      wrapperRef={wrapperRef}
      multiple={multiple}
      acceptType={acceptType}
      filesHandler={handleFilesUpload}
      isAreaDraggable={!isMobile}
      showWarning={showWarning}
    >
      <UploadAreaContent
        mediaLibConfig={mediaLibConfig}
        handleFilesUpload={handleFilesUpload}
        acceptType={acceptType}
        handleMediaLibFiles={handleMediaLibFiles}
        isLoading={isLoading}
        extraActions={extraActions}
      />
    </DragDropUpload>
  )
}
